import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import DescriptionIcon from '@mui/icons-material/Description'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import TextField from '@mui/material/TextField'
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker'
import dayjs from 'dayjs'
import { useAtom } from 'jotai'
import { useAtomValue } from 'jotai'
import * as React from 'react'
import { memo } from 'react'
import { shallowEqual } from 'react-redux'

import { instructorAtom } from '../../contexts/AtomPrivateSessionSearch'
import { currentUserAtom } from '../../domains/user/AtomUsers'
import { WAIVER_TEXT } from '../../share/Constant'
import { validateUserProfileForRegistration } from '../../utils/profileValidation'
import InfoSection from '../common/InfoSection'
import RegistrationReminder from '../dialog/RegistrationReminder'
import usePrivateSessionBooking from '../hooks/usePrivateSessionBooking'
import { useTimeSelection } from '../hooks/useTimeSelection'

/**
 * TimeSlotsBookingCard - A component to display selected time slots and booking confirmation dialog
 *
 * @param {Object} props - Component props
 * @param {Set} props.selectedTimeSlots - Set of selected time slots
 * @param {React.RefObject} props.bookingButtonRef - Reference to the booking button section
 * @param {boolean} props.isBooking - Flag indicating if booking is in progress
 * @param {Function} props.handleBookingClick - Function to handle booking button click
 * @param {boolean} props.openConfirmDialog - Flag indicating if confirmation dialog is open
 * @param {Function} props.setOpenConfirmDialog - Function to set confirmation dialog open state
 * @param {string} props.instructor - Selected instructor ID
 * @param {Object} props.instructorDetail - Instructor detail
 * @param {Array} props.availableSessions - Available sessions
 */
export const TimeSlotsBookingCard = () => {
  const user = useAtomValue(currentUserAtom)

  const {
    calendarDate,
    selectedDateString,
    selectedTimeSlots,
    availableTimeSlots,
    selectedSessions,
    selectedSessionsLocations,
    nextAvailableDate,
    prevAvailableDate,

    // 处理函数
    handleDateChange,
    handleTimeSlotSelect,
    handleMonthChange,
    handleNextDate,
    handlePrevDate,

    // 辅助函数
    shouldDisableDate,
  } = useTimeSelection()

  const [instructor] = useAtom(instructorAtom)

  const { handleOpenBookingDialog, handleConfirmBooking, isBooking, openConfirmDialog, setOpenConfirmDialog, error, bookingNotes, setBookingNotes } = usePrivateSessionBooking()

  const bookingButtonClick = React.useCallback(() => {
    handleOpenBookingDialog(instructor, selectedDateString, selectedTimeSlots)
  }, [handleOpenBookingDialog, instructor, selectedDateString, selectedTimeSlots])

  const confirmBookingClick = React.useCallback(() => {
    handleConfirmBooking(selectedSessions)
  }, [handleConfirmBooking, selectedSessions])

  // Create a ref for the booking button section
  const bookingButtonRef = React.useRef(null)
  // Scroll to booking button when it becomes visible
  React.useEffect(() => {
    if (selectedTimeSlots.size > 0 && bookingButtonRef.current) {
      // Check if element is not visible in viewport
      const rect = bookingButtonRef.current.getBoundingClientRect()
      const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight

      if (!isVisible) {
        // Only scroll if not visible, align to bottom 20% of viewport
        bookingButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        // window.scrollBy(0, window.innerHeight * 0.2)
      }
    }
  }, [selectedTimeSlots.size])

  const [waiverExpanded, setWaiverExpanded] = React.useState(true)
  const [waiverAgreed, setWaiverAgreed] = React.useState(false)

  // Reset waiver state when dialog closes
  React.useEffect(() => {
    if (!openConfirmDialog) {
      setWaiverAgreed(false)
    }
  }, [openConfirmDialog])

  const isProfileValid = validateUserProfileForRegistration(user).isValid

  return (
    <>
      {/* 预订确认对话框 */}
      {openConfirmDialog && (
        <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)} aria-labelledby="booking-confirm-dialog">
          <DialogTitle id="booking-confirm-dialog">Confirm Booking</DialogTitle>
          <DialogContent>
            {/* Session Information */}
            <InfoSection
              title="Session Information"
              info={{
                date: selectedDateString,
                timeSlot: Array.from(selectedTimeSlots).join(', '),
                location: selectedSessionsLocations.join(', '),
                sport: selectedSessions[0]?.sportType || selectedSessions[0]?.sport,
                price: selectedSessions[0]?.price ? `$${selectedSessions.reduce((total, session) => total + (session.price || 0), 0)} (${selectedSessions.length} sessions)` : ``,
                cancellationDeadline: selectedSessions[0]?.cancellationDeadline,
                registrationDeadline: selectedSessions[0]?.registrationDeadline,
                status: selectedSessions[0]?.status,
              }}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
              *Price could be different based on student number. Confirm with your instructor for the final price.
            </Typography>

            {/* 添加会话描述 */}
            {selectedSessions[0]?.description && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DescriptionIcon color="action" />
                    Private Session Description
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line', pl: 4 }}>
                    {selectedSessions[0]?.description}
                  </Typography>
                </Box>
              </>
            )}

            {/* Contact Information Reminder */}
            <RegistrationReminder user={user} />

            {/* Waiver Section */}
            <Divider sx={{ my: 2 }} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" color="text.secondary">
                  WAIVER AND RELEASE OF LIABILITY
                </Typography>
                <IconButton onClick={() => setWaiverExpanded(!waiverExpanded)} size="small">
                  {waiverExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={waiverExpanded}>
                <Box
                  sx={{
                    mt: 1,
                    p: 2,
                    bgcolor: 'warning.lighter',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'warning.light',
                  }}
                >
                  <Typography variant="h7" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    ACKNOWLEDGEMENT, WAIVER AND RELEASE OF LIABILITY
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {WAIVER_TEXT}
                  </Typography>
                </Box>
              </Collapse>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={waiverAgreed}
                    onChange={(e) => {
                      setWaiverAgreed(e.target.checked)
                      setWaiverExpanded(!e.target.checked)
                    }}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    I have read and agree to the waiver agreement{' '}
                    <Box component="span" sx={{ color: 'error.main' }}>
                      *
                    </Box>
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Session Notes: <br />
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Sending a note to your instructor can help them to prepare for the sessions.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              margin="normal"
              placeholder="e.g. I'm an intermediate level skier/snowboarder, please help me with carving."
              value={bookingNotes}
              onChange={(e) => setBookingNotes(e.target.value)}
              sx={{
                mt: 3,
                '& .MuiInputBase-root': {
                  minHeight: '150px',
                },
                '& .MuiOutlinedInput-root': {
                  padding: '16px',
                },
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirmDialog(false)} color="inherit">
              Cancel
            </Button>
            {waiverAgreed && isProfileValid && (
              <Button onClick={confirmBookingClick} variant="contained" disabled={isBooking}>
                {isBooking ? 'Booking...' : `Confirm Booking (${selectedTimeSlots.size} sessions)`}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}

      <Card sx={{ minWidth: 320 }}>
        <CardContent>
          {/* 日期选择器 */}
          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            value={calendarDate}
            onChange={handleDateChange}
            penTo="month"
            onMonthChange={handleMonthChange}
            disablePast
            views={['day']}
            shouldDisableDate={shouldDisableDate}
          />
          {
            <>
              <Divider sx={{ my: 2 }} />
              <FormControl fullWidth>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <Tooltip title={prevAvailableDate ? `Previous available date is ${dayjs(prevAvailableDate).format('MM-DD')}` : 'No earlier dates available'}>
                    <span>
                      <Button variant="text" size="small" onClick={handlePrevDate} disabled={!prevAvailableDate}>
                        <ArrowBackIcon fontSize="small" />
                      </Button>
                    </span>
                  </Tooltip>

                  <Stack direction="column" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                    <FormLabel
                      sx={{
                        flex: 1,
                        textAlign: 'center',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        letterSpacing: '0.15rem',
                      }}
                    >
                      {dayjs(calendarDate).format('MMMM D, YYYY')}
                    </FormLabel>
                    <FormLabel
                      sx={{
                        flex: 1,
                        textAlign: 'center',
                        fontSize: '0.7rem',
                        letterSpacing: '0.15rem',
                      }}
                    >
                      {availableTimeSlots.length > 0 ? 'Available Time Slots' : 'No Available Time Slots'}
                    </FormLabel>
                  </Stack>

                  <Tooltip title={nextAvailableDate ? `Next available date is ${dayjs(nextAvailableDate).format('MM-DD')}` : 'No later dates available'}>
                    <span>
                      <Button variant="text" size="small" onClick={handleNextDate} disabled={!nextAvailableDate}>
                        <ArrowForwardIcon fontSize="small" />
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>

                {/* 时间槽列表 */}
                {availableTimeSlots.length > 0 && (
                  <Stack spacing={1}>
                    {availableTimeSlots.map(({ timeSlot, session }) => (
                      <Button
                        key={timeSlot}
                        variant={selectedTimeSlots.has(timeSlot) ? 'contained' : 'outlined'}
                        onClick={() => handleTimeSlotSelect(timeSlot, session)}
                        sx={{
                          justifyContent: 'center',
                          py: 1.5,
                          borderRadius: 2,
                          borderWidth: 1,
                          textTransform: 'none',
                          fontSize: '1rem',
                        }}
                      >
                        {timeSlot}
                      </Button>
                    ))}
                  </Stack>
                )}
              </FormControl>
            </>
          }
        </CardContent>

        {/* 预订按钮 */}
        {selectedDateString && instructor && selectedTimeSlots.size > 0 && (
          <Box
            ref={bookingButtonRef}
            sx={{
              alignItems: 'center',
              p: 2,
              bgcolor: 'background.paper',
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Divider sx={{ my: 2 }} />
            <Button fullWidth variant="contained" size="large" onClick={bookingButtonClick} disabled={isBooking}>
              {isBooking ? 'Booking...' : 'Book Now'}
            </Button>
          </Box>
        )}
      </Card>
    </>
  )
}

export default memo(TimeSlotsBookingCard, shallowEqual)
